import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { getParsedCookies, Language } from '@/shared/utils';

const LANGUAGE_COOKIE_NAME = 'language';
const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

type LanguageContextType = {
  language: Language;
  handleLanguageChange: (value: Language) => void;
  isLanguageCookieSet: boolean;
};

const LanguageContext = createContext<LanguageContextType>({
  handleLanguageChange: () => {},
  language: 'en',
  isLanguageCookieSet: isCookieSet(LANGUAGE_COOKIE_NAME),
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }: PropsWithChildren) => {
  const parsedCookies = getParsedCookies();

  const defaultLanguage: Language = parsedCookies[LANGUAGE_COOKIE_NAME]
    ? (parsedCookies[LANGUAGE_COOKIE_NAME] as Language)
    : 'en';

  const isLanguageCookieSet = isCookieSet(LANGUAGE_COOKIE_NAME);

  const [language, setLanguage] = useState<Language>(defaultLanguage);

  const handleLanguageChange = useCallback((value: Language) => {
    setLanguage(value);
    document.cookie = `${LANGUAGE_COOKIE_NAME}=${value}; path=/; max-age=${LANGUAGE_COOKIE_MAX_AGE}`;
  }, []);

  const values = useMemo<LanguageContextType>(
    () => ({
      handleLanguageChange,
      language,
      isLanguageCookieSet,
    }),
    [handleLanguageChange, isLanguageCookieSet, language],
  );

  return <LanguageContext.Provider value={values}>{children}</LanguageContext.Provider>;
};

function isCookieSet(cookieName: string): boolean {
  return document.cookie
    .split('; ')
    .some((cookie: string) => cookie.startsWith(`${cookieName}=`));
}
